import { t, type I18nChildContext } from "@bookingcom/lingojs-react";

export const CAMPAIGN_IMPACT_METRICS_AVAILABLE_DATE = new Date("2026-04-01");

export const CAMPAIGN_IMPACT_METRIC_NAMES = [
  "UPLIFT_IN_BOOKINGS",
  "UPLIFT_IN_PASSENGERS",
  "UPLIFT_IN_BOOKINGS_PERCENTAGE",
  "UPLIFT_IN_PASSENGERS_PERCENTAGE",
  "CAMPAIGN_EFFECTIVENESS",
] as const;

/**
 * Absolute-count metrics that back the green subtitle beneath the uplift headlines.
 */
export const CAMPAIGN_IMPACT_ABSOLUTE_METRIC_NAMES = [
  "UPLIFT_IN_PASSENGERS",
  "UPLIFT_IN_BOOKINGS",
] as const satisfies readonly (typeof CAMPAIGN_IMPACT_METRIC_NAMES)[number][];

export const PeriodType = { DAYS: "DAYS", MONTHS: "MONTHS" } as const;
export type PeriodTypeValue = (typeof PeriodType)[keyof typeof PeriodType];

export type CampaignImpactPeriodGroup = "recent" | "month";

export type CampaignImpactPeriodOption = {
  periodType: PeriodTypeValue;
  periodValue: number;
  group: CampaignImpactPeriodGroup;
  labelVars?: Record<string, number | string>;
};

export const CAMPAIGN_IMPACT_PERIOD_OPTIONS: CampaignImpactPeriodOption[] = [
  { periodType: PeriodType.DAYS, periodValue: 7, group: "recent", labelVars: { num_days: 7 } },
  { periodType: PeriodType.DAYS, periodValue: 14, group: "recent", labelVars: { num_days: 14 } },
  { periodType: PeriodType.DAYS, periodValue: 21, group: "recent", labelVars: { num_days: 21 } },
  { periodType: PeriodType.DAYS, periodValue: 28, group: "recent", labelVars: { num_days: 28 } },
];

export const getMonthPeriodOptions = (now: Date = new Date()): CampaignImpactPeriodOption[] => {
  const totalMonths =
    (now.getFullYear() - CAMPAIGN_IMPACT_METRICS_AVAILABLE_DATE.getFullYear()) * 12 +
    (now.getMonth() - CAMPAIGN_IMPACT_METRICS_AVAILABLE_DATE.getMonth());

  return Array.from({ length: totalMonths }, (_, i) => ({
    periodType: PeriodType.MONTHS,
    periodValue: i + 1,
    group: "month" as const,
  }));
};

export const formatMonthLabel = (periodValue: number, now: Date = new Date(), locale = "en"): string => {
  const date = new Date(now.getFullYear(), now.getMonth() - periodValue, 1);
  return new Intl.DateTimeFormat(locale, { month: "long", year: "numeric" }).format(date);
};

export const getAllPeriodOptions = (now?: Date): CampaignImpactPeriodOption[] => [
  ...CAMPAIGN_IMPACT_PERIOD_OPTIONS,
  ...getMonthPeriodOptions(now),
];

export const toPeriodKey = (periodType: PeriodTypeValue, periodValue: number): string => `${periodType}_${periodValue}`;

export const DEFAULT_CAMPAIGN_IMPACT_PERIOD = toPeriodKey(PeriodType.DAYS, 14);

type MetricBoxConfig = {
  metricName: (typeof CAMPAIGN_IMPACT_METRIC_NAMES)[number];
  label: string;
  tooltip: string;
  subtitleMetricName?: (typeof CAMPAIGN_IMPACT_ABSOLUTE_METRIC_NAMES)[number];
};

export const getMetricBoxConfig = (i18n: I18nChildContext): MetricBoxConfig[] => [
  {
    metricName: "UPLIFT_IN_PASSENGERS_PERCENTAGE",
    label: i18n.trans(t("flights_airline_portal_airline_view_campaign_metric_two_name")),
    tooltip: i18n.trans(t("flights_airline_portal_airline_view_campaign_metric_two_tooltip")),
    subtitleMetricName: "UPLIFT_IN_PASSENGERS",
  },
  {
    metricName: "UPLIFT_IN_BOOKINGS_PERCENTAGE",
    label: i18n.trans(t("flights_airline_portal_airline_view_campaign_metric_one_name")),
    tooltip: i18n.trans(t("flights_airline_portal_airline_view_campaign_metric_one_tooltip")),
    subtitleMetricName: "UPLIFT_IN_BOOKINGS",
  },
  {
    metricName: "CAMPAIGN_EFFECTIVENESS",
    label: i18n.trans(t("flights_airline_portal_airline_view_campaign_metric_three_name")),
    tooltip: i18n.trans(t("flights_airline_portal_airline_view_campaign_metric_three_tooltip")),
  },
];
